import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SetlistCard from '../components/SetlistCard';
import StandardPageHeader from '../components/StandardPageHeader';
import { ArrowLeftIcon, UsersIcon, MusicIcon, MapPinIcon, GlobeIcon, PlusIcon, CalendarIcon, Edit, MoreVertical, Trash, UserMinus, CheckCircle, XCircle, HelpCircle, ArrowRight, LinkIcon } from 'lucide-react';
import { Button } from "../components/ui/button";
import { useAuth } from '../contexts/AuthContext';
import eventBus from '../utils/EventBus';
import BandLineupManager from '../components/BandLineupManager';
import CommentsSection from '../components/Comments/CommentsSection';
import EventCard from '../components/EventCard';
import { formatDateTime, formatTime } from '../utils/dateFormatter';
import { API_URL, fetchWithAuth } from '../services/ApiService';

/**
 * Individual Band Page - shows band details and associated setlists
 */
const BandPage = () => {
  const { bandId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();
  const [band, setBand] = useState(null);
  const [setlists, setSetlists] = useState([]);
  const [userSetlists, setUserSetlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddSetlistModal, setShowAddSetlistModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ role: 'member', maxUses: 1, expiresInDays: 7 });
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [events, setEvents] = useState([]);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rsvpForm, setRsvpForm] = useState({ name: '', email: '', status: '' });

  // Fetch band details
  const fetchBandDetails = async () => {
    try {
      const response = await fetch(`/api/bands/${bandId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        setBand(result.data);
      } else if (response.status === 404) {
        setError('Band not found');
      } else if (response.status === 403) {
        setError('You do not have permission to view this band');
      } else {
        setError('Failed to load band details');
      }
    } catch (error) {
      console.error('Error fetching band details:', error);
      setError('Failed to load band details');
    }
  };

  // Fetch band setlists
  const fetchBandSetlists = async () => {
    try {
      const response = await fetch(`/api/setlists?band_id=${bandId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        setSetlists(result.data || []);
      } else {
        console.error('Failed to fetch band setlists');
      }
    } catch (error) {
      console.error('Error fetching band setlists:', error);
    }
  };

  // Fetch user's setlists that aren't associated with this band
  const fetchUserSetlists = async () => {
    try {
      const response = await fetch('/api/setlists', {
        credentials: 'include'
      });

      if (response.ok) {
        const result = await response.json();
        const allSetlists = result.data || [];
        // Filter out setlists that are already associated with this band
        const unassociatedSetlists = allSetlists.filter(setlist => 
          !setlist.band_id || setlist.band_id !== bandId
        );
        setUserSetlists(unassociatedSetlists);
      } else {
        console.error('Failed to fetch user setlists');
      }
    } catch (error) {
      console.error('Error fetching user setlists:', error);
    }
  };

  // Fetch band events (works for both authenticated and public users)
  const fetchBandEvents = async () => {
    try {
      // For events, we need to use the optionalAuth endpoint
      // So we make a direct fetch call with credentials: 'include' which works for both cases
      const response = await fetch(`${API_URL}/events?band=${bandId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include' // This will include cookies if present, but won't fail if not
      });
      
      if (response.ok) {
        const result = await response.json();
        const bandEvents = result.data || [];
        // Sort by date, upcoming first
        const now = new Date();
        const sortedEvents = bandEvents
          .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
          .filter(event => new Date(event.startTime) > now); // Only show future events
        setEvents(sortedEvents);
        console.log('Fetched events for band:', bandId, sortedEvents.length);
      } else {
        console.error('Failed to fetch events:', response.status);
      }
    } catch (error) {
      console.error('Error fetching band events:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchBandDetails(),
        fetchBandSetlists(),
        fetchBandEvents(),
        isAuthenticated ? fetchUserSetlists() : Promise.resolve()
      ]);
      setLoading(false);
    };

    if (bandId) {
      loadData();
    }
  }, [bandId]);

  // Add listener for when returning from setlist creation
  useEffect(() => {
    const handleFocus = () => {
      // Refresh setlists when window regains focus (user returns from creating setlist)
      if (bandId && isAuthenticated) {
        fetchBandSetlists();
        fetchUserSetlists();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [bandId, isAuthenticated]);

  // Set up event listeners for band page actions
  useEffect(() => {
    const editBandListener = eventBus.on('bandPage:editBand', () => {
      if (canManageBand()) {
        handleEditBand();
      }
    });

    const newSetlistListener = eventBus.on('bandPage:newSetlist', () => {
      if (isAuthenticated) {
        handleCreateSetlist();
      }
    });

    const addExistingSetlistListener = eventBus.on('bandPage:addExistingSetlist', () => {
      if (isAuthenticated && userSetlists.length > 0) {
        setShowAddSetlistModal(true);
      }
    });

    const inviteListener = eventBus.on('bandPage:invite', () => {
      if (canManageBand()) {
        // Trigger the invite form by setting the first band card's invite form state
        // Since we're on the band page, we need to find a way to show the invite interface
        // We'll scroll to the band details section and show an inline invite form
        setShowInviteModal(true);
      }
    });

    return () => {
      editBandListener();
      newSetlistListener();
      addExistingSetlistListener();
      inviteListener();
    };
  }, [bandId, isAuthenticated, userSetlists.length, band]);

  // Associate existing setlist with this band
  const associateSetlistWithBand = async (setlistId) => {
    try {
      const response = await fetch(`/api/setlists/${setlistId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          band_id: bandId
        }),
      });

      if (response.ok) {
        // Refresh both lists
        await Promise.all([
          fetchBandSetlists(),
          fetchUserSetlists()
        ]);
        setShowAddSetlistModal(false);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to associate setlist with band');
      }
    } catch (error) {
      console.error('Error associating setlist with band:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Remove setlist from band (make it personal again)
  const removeSetlistFromBand = async (setlistId) => {
    if (!window.confirm('Remove this setlist from the band? It will become a personal setlist.')) {
      return;
    }

    try {
      const response = await fetch(`/api/setlists/${setlistId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          band_id: null
        }),
      });

      if (response.ok) {
        // Refresh both lists
        await Promise.all([
          fetchBandSetlists(),
          fetchUserSetlists()
        ]);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove setlist from band');
      }
    } catch (error) {
      console.error('Error removing setlist from band:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const canManageBand = () => {
    return band && band.user_role === 'admin';
  };

  // Handle public RSVP submission
  const handlePublicRSVP = async () => {
    // For authenticated users, use their account info
    if (isAuthenticated) {
      if (!selectedEvent || !rsvpForm.status) {
        alert('Please select a response');
        return;
      }
      
      // Use the authenticated RSVP endpoint
      try {
        const result = await fetchWithAuth(`${API_URL}/events/${selectedEvent._id}/rsvp`, {
          method: 'POST',
          body: JSON.stringify({
            status: rsvpForm.status
          })
        });

        if (result.success) {
          await fetchBandEvents();
          setShowRSVPModal(false);
          setSelectedEvent(null);
          setRsvpForm({ name: '', email: '', status: '' });
        } else {
          alert(result.error || 'Failed to submit RSVP');
        }
      } catch (error) {
        console.error('Error submitting RSVP:', error);
        alert('Failed to submit RSVP');
      }
      return;
    }
    
    // For non-authenticated users, require name
    if (!selectedEvent || !rsvpForm.name || !rsvpForm.status) {
      alert('Please enter your name and select a response');
      return;
    }

    try {
      // Public RSVP doesn't require authentication
      const response = await fetch(`${API_URL}/events/${selectedEvent._id}/public-rsvp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(rsvpForm)
      });

      if (response.ok) {
        const result = await response.json();
        // Save name/email for future RSVPs
        localStorage.setItem('rsvp_name', rsvpForm.name);
        if (rsvpForm.email) {
          localStorage.setItem('rsvp_email', rsvpForm.email);
        }
        
        // Refresh events to show updated RSVP
        await fetchBandEvents();
        setShowRSVPModal(false);
        setSelectedEvent(null);
        setRsvpForm({ name: '', email: '', status: '' });
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to submit RSVP');
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      alert('Failed to submit RSVP');
    }
  };

  // Initialize RSVP form with saved data
  const openRSVPModal = (event) => {
    setSelectedEvent(event);
    setRsvpForm({
      name: localStorage.getItem('rsvp_name') || '',
      email: localStorage.getItem('rsvp_email') || '',
      status: ''
    });
    setShowRSVPModal(true);
  };


  const handleCreateSetlist = () => {
    navigate(`/setlist/new?band_id=${bandId}`);
  };

  const handleEditBand = () => {
    navigate(`/bands?edit=${bandId}`);
  };

  // Generate invite link
  const handleGenerateInvite = async () => {
    try {
      const response = await fetch(`/api/bands/${bandId}/invites`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(inviteForm),
      });

      if (response.ok) {
        const result = await response.json();
        setGeneratedInvite(result.data);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate invite');
      }
    } catch (error) {
      console.error('Error generating invite:', error);
      alert(`Error generating invite: ${error.message}`);
    }
  };

  // Copy invite link to clipboard
  const handleCopyInviteLink = async () => {
    if (!generatedInvite?.invite_url) return;

    try {
      await navigator.clipboard.writeText(generatedInvite.invite_url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generatedInvite.invite_url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  // Remove member from band
  const removeMember = async (member) => {
    const isRemovingSelf = member.user?._id === currentUser?._id;
    const memberName = member.user?.username || 'this user';
    
    const confirmMessage = isRemovingSelf
      ? `Are you sure you want to leave the band "${band.name}"?`
      : `Are you sure you want to remove ${memberName} from the band "${band.name}"?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      const response = await fetch(`/api/bands/${bandId}/members/${member._id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        // Refresh band details to update member list
        await fetchBandDetails();
        
        // If user removed themselves, redirect to bands page
        if (isRemovingSelf) {
          navigate('/bands');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to remove member');
      }
    } catch (error) {
      console.error('Error removing member:', error);
      alert(`Error: ${error.message}`);
    }
  };



  // Member Card Component
  const MemberCard = ({ member }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!showDropdown) return;
      
      const handleClickOutside = (e) => {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setShowDropdown(false);
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);

    const canManageMember = () => {
      if (!isAuthenticated) return false;
      // Admins can manage any member, or users can leave themselves
      return canManageBand() || member.user?._id === currentUser?._id;
    };

    const isRemovingSelf = member.user?._id === currentUser?._id;

    const handleMemberClick = () => {
      if (member.user?._id) {
        navigate(`/profile/${member.user._id}`);
      }
    };

    return (
      <div className="flex items-center justify-between bg-background rounded-md p-3 relative">
        <div 
          className="flex items-center flex-1 cursor-pointer hover:bg-gray-50 rounded-md p-1 -m-1 transition-colors"
          onClick={handleMemberClick}
        >
          <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center mr-3">
            <span className="text-xs font-medium">
              {member.user?.username?.charAt(0).toUpperCase() || '?'}
            </span>
          </div>
          <div>
            <div className="text-sm font-medium">{member.user?.username || 'Unknown'}</div>
            <div className="text-xs text-muted-foreground">{member.role}</div>
          </div>
        </div>
        
        {/* Dropdown menu */}
        {canManageMember() && (
          <div 
            className="relative" 
            ref={dropdownRef}
          >
            <button 
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowDropdown(!showDropdown);
              }}
              className="p-1 rounded-md hover:bg-background flex-shrink-0"
            >
              <MoreVertical className="h-4 w-4 text-muted-foreground" />
            </button>
            
            {showDropdown && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-border">
                <button 
                  className="flex items-center w-full px-4 py-2 text-sm text-left hover:bg-gray-100 text-red-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDropdown(false);
                    removeMember(member);
                  }}
                >
                  <UserMinus className="h-4 w-4 mr-2" />
                  {isRemovingSelf ? 'Leave Band' : 'Remove Member'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="text-center py-20">
          <p className="text-muted-foreground">Loading band details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="text-center py-20">
          <p className="text-red-600 mb-4">{error}</p>
          <Button variant="outline" onClick={() => navigate('/bands')}>
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Bands
          </Button>
        </div>
      </div>
    );
  }

  if (!band) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="text-center py-20">
          <p className="text-muted-foreground">Band not found</p>
          <Button variant="outline" onClick={() => navigate('/bands')} className="mt-4">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Bands
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Standard page header with back button, title, and actions */}
      <StandardPageHeader
        title={band.name}
        backTo="/bands"
        backLabel="Bands"
        actions={
          <>
            {band.user_role && (
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                band.user_role === 'admin'
                  ? 'bg-red-100 text-red-800 border border-red-300'
                  : 'bg-blue-100 text-blue-800 border border-blue-300'
              }`}>
                {band.user_role}
              </span>
            )}
            {canManageBand() && (
              <>
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
                >
                  <LinkIcon className="w-3 h-3" />
                  <span className="hidden sm:inline">Invite Members</span>
                </button>
                <button
                  onClick={handleEditBand}
                  className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-md flex items-center gap-1.5 transition-colors"
                >
                  <Edit className="w-3 h-3" />
                  <span className="hidden sm:inline">Edit Band</span>
                </button>
              </>
            )}
          </>
        }
      />

      <div className="p-4">

      {/* Band Details */}
      <div className="bg-card border border-border rounded-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            {band.description && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Description</h3>
                <p className="text-sm">{band.description}</p>
              </div>
            )}

            <div className="space-y-2">
              {band.genre && (
                <div className="flex items-center text-sm">
                  <MusicIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                  <span className="text-muted-foreground mr-2">Genre:</span>
                  <span>{band.genre}</span>
                </div>
              )}
              {band.location && (
                <div className="flex items-center text-sm">
                  <MapPinIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                  <span className="text-muted-foreground mr-2">Location:</span>
                  <span>{band.location}</span>
                </div>
              )}
              {band.website && (
                <div className="flex items-center text-sm">
                  <GlobeIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                  <span className="text-muted-foreground mr-2">Website:</span>
                  <a 
                    href={band.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {band.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Statistics</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Members:</span>
                <span>{band.stats?.member_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Sheets:</span>
                <span>{band.stats?.sheet_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Setlists:</span>
                <span>{band.stats?.setlist_count || 0}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-xs text-muted-foreground">
                Created: {formatDate(band.createdAt || band.created_at)}
              </div>
            </div>
          </div>
        </div>

        {/* Members */}
        {band.members && band.members.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Members</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {band.members.map(member => (
                <MemberCard key={member._id} member={member} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Setlists */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Setlists</h2>
        </div>

        {setlists.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
            <p className="text-muted-foreground">No setlists yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {setlists.map(setlist => (
              <SetlistCard 
                key={setlist._id} 
                setlist={setlist}
                canManage={canManageBand() || setlist.owner?._id === currentUser?._id}
                context="band"
                onRemoveFromBand={removeSetlistFromBand}
              />
            ))}
          </div>
        )}
      </div>

      {/* Band Lineups */}
      <div className="mb-6">
        <BandLineupManager band={band} onUpdate={fetchBandDetails} />
      </div>

      {/* Upcoming Events */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2" />
            Upcoming Events
          </h2>
          {events.length > 0 && (
            <button
              onClick={() => navigate('/events')}
              className="text-sm text-primary hover:text-primary/80 hover:underline"
            >
              View all events →
            </button>
          )}
        </div>

        {events.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-border rounded-md bg-background">
            <p className="text-muted-foreground">No upcoming events</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map(event => (
              <EventCard 
                key={event._id} 
                event={event} 
                onMenuClick={openRSVPModal}
              />
            ))}
          </div>
        )}
      </div>

      {/* Comments Section */}
      {band && (
        <div className="mt-6">
          <CommentsSection 
            objectType="band"
            objectId={band._id}
            collapsed={false}
            printMode={false}
          />
        </div>
      )}
      </div>

      {/* Add Existing Setlist Modal */}
      {showAddSetlistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Existing Setlist</h3>
              <button
                onClick={() => setShowAddSetlistModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-3">
                Select a setlist to associate with this band:
              </p>
              
              {userSetlists.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No available setlists to add
                </div>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {userSetlists.map(setlist => (
                    <div
                      key={setlist._id}
                      className="border border-gray-200 rounded-md p-3 hover:bg-gray-50 cursor-pointer"
                      onClick={() => associateSetlistWithBand(setlist._id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-sm">{setlist.name}</h4>
                          {setlist.description && (
                            <p className="text-xs text-gray-500 mt-1">{setlist.description}</p>
                          )}
                          <div className="flex items-center text-xs text-gray-400 mt-1">
                            <CalendarIcon className="w-3 h-3 mr-1" />
                            <span>{formatDate(setlist.createdAt || setlist.created_at)}</span>
                            <span className="ml-3">{setlist.sheets?.length || 0} sheets</span>
                          </div>
                        </div>
                        <PlusIcon className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={() => setShowAddSetlistModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Members Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Invite Members to {band?.name}</h3>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setGeneratedInvite(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Generate an invite link to add new members to your band:
              </p>
              
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={inviteForm.role}
                    onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={inviteForm.maxUses}
                    onChange={(e) => setInviteForm({ ...inviteForm, maxUses: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Days</label>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={inviteForm.expiresInDays}
                    onChange={(e) => setInviteForm({ ...inviteForm, expiresInDays: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={handleGenerateInvite}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors mb-4"
              >
                Generate Invite Link
              </button>

              {/* Generated Invite */}
              {generatedInvite && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <h5 className="font-medium text-green-800 mb-2 text-sm">Invite Link Generated!</h5>
                  <p className="text-sm text-green-700 mb-3">
                    Role: {generatedInvite.role} | Expires: {formatDate(generatedInvite.expires_at)}
                  </p>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={generatedInvite.invite_url}
                      readOnly
                      className="flex-1 px-3 py-2 bg-white border border-green-300 rounded-md text-sm"
                    />
                    <button
                      onClick={handleCopyInviteLink}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        copiedLink 
                          ? 'bg-green-600 text-white' 
                          : 'bg-green-100 hover:bg-green-200 text-green-700'
                      }`}
                    >
                      {copiedLink ? (
                        <>
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setGeneratedInvite(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* RSVP Modal */}
      {showRSVPModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">RSVP for {selectedEvent.title}</h3>
              <button
                onClick={() => {
                  setShowRSVPModal(false);
                  setSelectedEvent(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="mb-4 text-sm text-gray-600">
              <p><strong>When:</strong> {formatDateTime(selectedEvent.startTime)}</p>
              {selectedEvent.location?.name && (
                <p><strong>Where:</strong> {selectedEvent.location.name}</p>
              )}
            </div>

            <div className="space-y-4">
              {isAuthenticated ? (
                <div className="p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-800">
                    Responding as: <strong>{currentUser?.username || currentUser?.email}</strong>
                  </p>
                </div>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Your Name *</label>
                    <input
                      type="text"
                      value={rsvpForm.name}
                      onChange={(e) => setRsvpForm({ ...rsvpForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Enter your name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Email (Optional)</label>
                    <input
                      type="email"
                      value={rsvpForm.email}
                      onChange={(e) => setRsvpForm({ ...rsvpForm, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="your@email.com"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Will you attend? *</label>
                <div className="space-y-2">
                  <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="status"
                      value="confirmed"
                      checked={rsvpForm.status === 'confirmed'}
                      onChange={(e) => setRsvpForm({ ...rsvpForm, status: e.target.value })}
                      className="mr-3"
                    />
                    <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                    <span>Yes, I'll be there!</span>
                  </label>

                  <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="status"
                      value="declined"
                      checked={rsvpForm.status === 'declined'}
                      onChange={(e) => setRsvpForm({ ...rsvpForm, status: e.target.value })}
                      className="mr-3"
                    />
                    <XCircle className="w-4 h-4 mr-2 text-red-600" />
                    <span>No, I can't make it</span>
                  </label>

                  <label className="flex items-center p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="status"
                      value="maybe"
                      checked={rsvpForm.status === 'maybe'}
                      onChange={(e) => setRsvpForm({ ...rsvpForm, status: e.target.value })}
                      className="mr-3"
                    />
                    <HelpCircle className="w-4 h-4 mr-2 text-yellow-600" />
                    <span>Maybe</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowRSVPModal(false);
                  setSelectedEvent(null);
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handlePublicRSVP}
                disabled={!rsvpForm.status || (!isAuthenticated && !rsvpForm.name)}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {isAuthenticated ? 'Submit RSVP' : 'Submit RSVP'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BandPage;